// Temporal polyfill for Schedule-X calendar (must be first import)
import 'temporal-polyfill/global'

import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { initSupabase } from './lib/supabase'
import { setupChunkErrorRecovery } from './lib/chunkErrorRecovery'

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { LocaleProvider } from './contexts/LocaleContext'
// Layouts
import GlobalLayout from './components/layout/GlobalLayout'
import AuthLayout from './components/layout/AuthLayout'

// Pages - Auth (static — on the critical path, shown immediately)
import LoginPage from './pages/auth/LoginPage'
import SignupPage from './pages/auth/SignupPage'
import CallbackPage from './pages/auth/CallbackPage'
import OnboardingPage from './pages/auth/OnboardingPage'

// Pages - Main (lazy loaded — each page is its own chunk)
const HomePage = React.lazy(() => import('./pages/HomePage'))
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'))
const AgentsPage = React.lazy(() => import('./pages/AgentsPage'))
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'))
const ChannelsPage = React.lazy(() => import('./pages/ChannelsPage'))
const InboxPage = React.lazy(() => import('./pages/InboxPage'))
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'))
const KnowledgePage = React.lazy(() => import('./pages/KnowledgePage'))
const PersonalityPage = React.lazy(() => import('./pages/personality/PersonalitySettings'))
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'))
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'))
const SuggestionsPage = React.lazy(() => import('./pages/SuggestionsPage'))
const AppsPage = React.lazy(() => import('./pages/AppsPage'))
const ConversationsPage = React.lazy(() => import('./pages/ConversationsPage'))
const OrdersPage = React.lazy(() => import('./pages/Orders'))
const OrderDetailPage = React.lazy(() => import('./pages/Orders/OrderDetail'))
const ConnectionsPage = React.lazy(() => import('./pages/settings/ConnectionsPage'))

// Styles
import './styles/globals.css'

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Protected Route wrapper - checks auth AND organization membership
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, needsOnboarding, isCheckingOrgs } = useAuth()
  const location = window.location.pathname

  // Show minimal spinner while verifying session (prevents blank screen flash)
  if (isLoading || isCheckingOrgs) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--background-color, #f8fafc)' }}>
        <div style={{ width: 24, height: 24, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', opacity: 0.3 }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Authenticated but needs onboarding - redirect to onboarding
  // (unless already on onboarding page to prevent loop)
  if (needsOnboarding && location !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

function App() {
  const routes = (
    <Routes>
      {/* Redirect root to home */}
      <Route path="/" element={<Navigate to="/home" replace />} />

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
      </Route>

      {/* Main protected routes */}
      <Route element={<ProtectedRoute><GlobalLayout /></ProtectedRoute>}>
        <Route path="/home" element={<HomePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/agents/:agentSlug/:tab" element={<AgentsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/channels" element={<ChannelsPage />} />
        <Route path="/conversations" element={<ConversationsPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/:agentId/:orderId" element={<OrderDetailPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/personality" element={<PersonalityPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/connections" element={<ConnectionsPage />} />
        <Route path="/suggestions" element={<SuggestionsPage />} />
        <Route path="/apps" element={<AppsPage />} />
      </Route>

      {/* 404 catch-all */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  )

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LocaleProvider>
          <ThemeProvider>
            <AuthProvider>
              {routes}
            </AuthProvider>
          </ThemeProvider>
        </LocaleProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

// Bootstrap runtime config (fetches /config.json) before rendering, so
// Supabase + any other services can be constructed synchronously inside
// effects. One Docker image works on every environment; nginx serves
// /config.json populated from container env vars at start-up.
async function bootstrap() {
  // Wire chunk error recovery before anything else so it catches the very
  // first lazy import after a redeploy.
  setupChunkErrorRecovery()

  try {
    await initSupabase()
  } catch (err) {
    // Surface the failure prominently — without runtime config, Supabase
    // realtime/storage features will be unavailable. Render the app
    // anyway (auth/UI still works) but log the error.
    console.error('[bootstrap] runtime config load failed:', err)
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  )
}

bootstrap()
